import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Switch } from "../ui/switch";
import { API } from "../../App";
import { toYMD, toYMDInTimezone } from "../../utils/dateUtils";
import { useTimezone } from "../../contexts/TimezoneContext";
import { dialogContentOutsideGuards } from "../../utils/dialogOutsideGuards";

/**
 * Add/Edit dialog for a single expense entry.
 * Fields: Date, Description, Category, Amount, Recurring monthly flag, Notes.
 * Submits to POST/PUT /api/expenses.
 *
 * The default date is "today *in the business timezone*" (not the browser's)
 * — guards against the case where the user's browser is in a different
 * timezone than the business setting (e.g., a Pacific user logging into an
 * Eastern business at 11:30 PM PT would otherwise file expenses under the
 * wrong business day).
 */
const ExpenseFormDialog = ({
  open,
  onOpenChange,
  editing,          // null for create, expense object for edit
  categories = [],
  onSaved,          // callback(savedExpense) after successful save
}) => {
  const { timezone } = useTimezone();
  const todayInBusinessTz = () => toYMDInTimezone(new Date(), timezone);

  const [date, setDate] = useState(todayInBusinessTz());
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("Other");
  const [amount, setAmount] = useState("");
  const [isRecurring, setIsRecurring] = useState(false);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  // Reset / hydrate form when dialog opens or editing target changes
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setDate(editing.date || todayInBusinessTz());
      setDescription(editing.description || "");
      setCategory(editing.category || "Other");
      setAmount(String(editing.amount ?? ""));
      setIsRecurring(Boolean(editing.is_recurring));
      setNotes(editing.notes || "");
    } else {
      setDate(todayInBusinessTz());
      setDescription("");
      setCategory("Other");
      setAmount("");
      setIsRecurring(false);
      setNotes("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, timezone]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!description.trim()) { toast.error("Description is required"); return; }
    const amt = parseFloat(amount);
    if (!(amt > 0)) { toast.error("Amount must be greater than 0"); return; }
    if (!category) { toast.error("Category is required"); return; }

    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        date,
        description: description.trim(),
        category,
        amount: amt,
        is_recurring: isRecurring,
        notes: notes.trim(),
      };
      const res = editing
        ? await axios.put(`${API}/expenses/${editing.expense_id}`, payload, { headers })
        : await axios.post(`${API}/expenses`, payload, { headers });
      toast.success(editing ? "Expense updated" : "Expense added");
      onSaved?.(res.data);
      onOpenChange?.(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d?.msg || d).join(", ")
        : (typeof detail === "string" ? detail : (editing ? "Failed to update expense" : "Failed to add expense"));
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="expense-form-dialog" {...dialogContentOutsideGuards}>
        <DialogHeader>
          <DialogTitle className="font-['Manrope']">
            {editing ? "Edit Expense" : "Add Expense"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="expense-date">Date</Label>
            <Input
              id="expense-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              data-testid="expense-date"
            />
          </div>

          <div>
            <Label htmlFor="expense-description">Description</Label>
            <Input
              id="expense-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Office rent — February"
              required
              data-testid="expense-description"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="expense-category">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger id="expense-category" data-testid="expense-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c} data-testid={`expense-category-opt-${c}`}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="expense-amount">Amount ($)</Label>
              <Input
                id="expense-amount"
                type="number"
                step="0.01"
                min="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                required
                data-testid="expense-amount"
              />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-md border border-theme bg-theme-surface px-3 py-2">
            <div>
              <p className="text-sm font-medium text-foreground">Repeats monthly</p>
              <p className="text-xs text-muted-foreground">Mark recurring expenses (e.g. rent, software)</p>
            </div>
            <Switch
              checked={isRecurring}
              onCheckedChange={setIsRecurring}
              data-testid="expense-recurring"
            />
          </div>

          <div>
            <Label htmlFor="expense-notes">Notes (optional)</Label>
            <Textarea
              id="expense-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Vendor, invoice #, anything useful"
              data-testid="expense-notes"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="expense-cancel-btn">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} data-testid="expense-save-btn">
              {saving ? "Saving…" : (editing ? "Save changes" : "Add expense")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ExpenseFormDialog;
